export type EnvMode =
  | "local"
  | "development"
  | "staging"
  | "training"
  | "production";

const ENV_MODES: readonly EnvMode[] = [
  "local",
  "development",
  "staging",
  "training",
  "production",
];

export function isEnvMode(value: unknown): value is EnvMode {
  return typeof value === "string" && ENV_MODES.includes(value as EnvMode);
}

export interface AppEnv {
  envMode: EnvMode;
  apiBaseUrl: string;
  bridgeTimeoutMs: number;
}

/**
 * Build-time configuration, read and checked once at module load so a missing
 * or malformed value fails immediately and loudly instead of surfacing as an
 * undefined URL somewhere deep in a request.
 */
function readEnv(): AppEnv {
  const raw = import.meta.env;

  if (!isEnvMode(raw.VITE_ENV_MODE)) {
    throw new Error(
      `VITE_ENV_MODE must be one of ${ENV_MODES.join(", ")} (got "${String(raw.VITE_ENV_MODE)}")`,
    );
  }

  const apiBaseUrl = raw.VITE_API_BASE_URL;
  if (typeof apiBaseUrl !== "string" || apiBaseUrl.length === 0) {
    throw new Error("VITE_API_BASE_URL is required");
  }

  const bridgeTimeoutMs = Number(raw.VITE_BRIDGE_TIMEOUT_MS);
  if (!Number.isInteger(bridgeTimeoutMs) || bridgeTimeoutMs <= 0) {
    throw new Error(
      `VITE_BRIDGE_TIMEOUT_MS must be a positive integer (got "${String(raw.VITE_BRIDGE_TIMEOUT_MS)}")`,
    );
  }

  return { envMode: raw.VITE_ENV_MODE, apiBaseUrl, bridgeTimeoutMs };
}

export const env: AppEnv = readEnv();

const DEV_ENV_MODES: readonly EnvMode[] = ["local", "development", "training"];
export const isDevBuild: boolean = DEV_ENV_MODES.includes(env.envMode);
